<template>
  <v-card flat class="pa-4">
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="32" />
      <p class="mt-2 text--secondary">Loading module configuration...</p>
    </div>

    <!-- Form content -->
    <v-form v-else ref="form" v-model="isFormValid">
      <!-- KOA Core fields -->
      <div v-if="appCode === 'KOA' && moduleName === 'core'">
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="config.customerSpecificMailSender"
              label="Customer Mail Sending"
              hint="Puts your order notification mail address as a sender in all FoodOrder mails"
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.deliveryMethodPickup"
              label="Delivery Method Pickup"
              hint="Presents the option to choose between pickup and delivery"
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.expressLogin"
              label="Express Login"
              hint="Enables password-less login by only scanning a special qr code"
              persistent-hint
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="config.notificationMail"
              label="Notification Mail"
              hint="Order notifications will be sent to this mail address."
              persistent-hint
              :rules="emailRules"
              clearable
            />
          </v-col>
          <v-col cols="12">
            <v-combobox
              v-model="config.bccNotificationMail"
              label="BCC Notification Mail"
              hint="Multiple email addresses for notifications (press Enter after each)"
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              :rules="bccEmailRules"
              clearable
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model.number="config.maxAssortments"
              label="Max Assortments"
              hint="All assortments beyond this value will only be in the extandable category tab. Set to -1 for unlimited assortments."
              persistent-hint
              type="number"
              :rules="maxAssortmentsRules"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="config.mailFooterNote"
              label="Mail Signature"
              hint="HTML content for mail signature (supports HTML tags)."
              persistent-hint
              rows="3"
              auto-grow
              clearable
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="config.orderingHints"
              label="Ordering Hints"
              hint="HTML content for ordering hints (supports HTML tags), these will shown in the product list  notifications and in the order summary."
              persistent-hint
              rows="3"
              auto-grow
              clearable
            />
          </v-col>
        </v-row>
      </div>

      <!-- DDN Core fields -->
      <div v-else-if="appCode === 'DDN' && moduleName === 'core'">
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="config.deliveryNoteFulfillment"
              label="Delivery Note Fulfillment"
              hint="Enables sending data to FoodOffice."
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.pickupAndReturn"
              label="Pickup and Return"
              hint="Allows to show and process pickup and return orders."
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.invoicedDeliveryNotes"
              label="Invoiced Delivery Notes"
              hint="Enables to view, edit and send back invoiced delivery notes."
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.separateDeliveryPointsByCustomer"
              label="Separate Delivery Points By Customer"
              hint="Creates a separate delivery point for each customer even if the delivery address is the same."
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.internalNotesAndPhotos"
              label="Internal Notes and Photos"
              hint="Lets drivers add internal notes and photos to delivery notes for back-office review in FoodOffice."
              persistent-hint
            />
          </v-col>
        </v-row>
      </div>

      <!-- KOA AI Order Import fields -->
      <div v-else-if="isAiOrderImportModule">
        <!-- Email Service Auth Type Toggle -->
        <h2 class="mb-2 font-weight-bold">Email Service Configuration</h2>
        <v-divider class="mb-4"></v-divider>
        <v-row>
          <v-col class="d-flex">
            <v-btn-toggle v-model="config.emailServiceAuthType" mandatory class="my-2">
              <v-btn
                tile
                depressed
                value="password"
                :color="config.emailServiceAuthType === 'password' ? 'primary' : 'grey lighten-3'"
                :class="config.emailServiceAuthType === 'password' ? 'white--text' : 'grey--text text--darken-2'"
              >
                IMAP
              </v-btn>
              <v-btn
                tile
                depressed
                value="oauth"
                :color="config.emailServiceAuthType === 'oauth' ? 'primary' : 'grey lighten-3'"
                :class="config.emailServiceAuthType === 'oauth' ? 'white--text' : 'grey--text text--darken-2'"
              >
                OAuth
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <!-- OAuth Section -->
        <div v-if="config.emailServiceAuthType === 'oauth'">
          <h2 class="my-2 font-weight-bold">Microsoft OAuth Configuration</h2>
          <v-divider class="mb-4"></v-divider>

          <v-row v-if="isLoadingOAuthStatus">
            <v-col>
              <v-card outlined>
                <v-card-text class="text-center">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-4">Checking OAuth configuration...</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else-if="!oauthStatus.isActive">
            <v-col>
              <v-card outlined>
                <v-card-text>
                  <v-icon size="64" color="grey" class="mb-4">mdi-microsoft</v-icon>
                  <h3 class="mb-4">Microsoft OAuth Not Configured</h3>
                  <div class="mb-4 text--secondary">
                    No active OAuth authorization found for this customer.
                  </div>
                  <v-btn color="primary" large @click="redirectToOAuthAuthorization">
                    <v-icon left>mdi-login</v-icon>
                    Authorize with Microsoft
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col>
              <v-card outlined>
                <v-card-title class="success--text">
                  <v-icon left color="success">mdi-check-circle</v-icon>
                  OAuth Authorization Active
                </v-card-title>
                <v-btn color="primary" class="ml-4 mb-4" large @click="redirectToOAuthAuthorization">
                  <v-icon left>mdi-login</v-icon>
                  Re-authorize with Microsoft
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- IMAP Section -->
        <div v-if="config.emailServiceAuthType === 'password'">
          <h2 class="my-2 font-weight-bold">IMAP Configuration</h2>
          <v-divider class="mb-4"></v-divider>
          <v-row>
            <v-col cols="12">
              <div class="text--secondary mb-2">IMAP path</div>
              <v-text-field
                v-model="config.imapPath"
                label="pattern: 'mail.clustermail.de:993/imap/ssl/novalidate-cert'"
                :rules="imapPathRules"
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text--secondary mb-2">Username</div>
              <v-text-field
                v-model="config.imapUsername"
                label="login for IMAP mailbox (usually it is an email)"
                :rules="imapUsernameRules"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <div class="text--secondary mb-2">Password</div>
              <v-text-field
                v-model="config.imapPassword"
                :append-icon="showImapPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showImapPassword ? 'text' : 'password'"
                @click:append="showImapPassword = !showImapPassword"
                label="password for IMAP mailbox"
                :rules="imapPasswordRules"
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-right">
              <v-btn
                color="info"
                @click="testImapConfig"
                :loading="isTestingImap"
                :disabled="isTestingImap || !isImapFormValid"
              >
                Test IMAP Connection
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4" />

        <!-- Common fields -->
        <h2 class="mb-2 font-weight-bold">Order Import Settings</h2>
        <v-divider class="mb-4"></v-divider>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="config.defaultFoAccountId"
              label="Default FO Account ID"
              hint="AI orders will be assigned to this account if no account could be determined otherwise"
              persistent-hint
              clearable
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="config.supportLeadingZerosForFoProductId"
              label="Support Leading Zeros for FO Product ID"
              hint="Up to four leading zeros will be ignored when trying to find the product"
              persistent-hint
            />
          </v-col>
        </v-row>

        <!-- Flowise API Key (SUPER_ADMIN only) -->
        <div v-if="isSuperAdmin">
          <v-divider class="my-4" />
          <h2 class="mb-2 font-weight-bold">Flowise Configuration</h2>
          <v-divider class="mb-4"></v-divider>
          <v-row>
            <v-col cols="12">
              <div class="text--secondary mb-2">Flowise API Key</div>
              <v-text-field
                v-model="config.flowiseApiKey"
                :type="flowiseApiKeyFieldType"
                label="Customer-specific Flowise API key for cost attribution"
                :hint="flowiseApiKeyHint"
                persistent-hint
                clearable
              />
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Generic fallback for unknown/not-yet-implemented modules -->
      <div v-else>
        <v-alert type="info" outlined>
          Configuration form for module "{{ formatModuleName(moduleName) }}" in app "{{ getAppDisplayName(appCode) }}" is not yet implemented.
        </v-alert>
      </div>

      <!-- Save button - only show for implemented modules with valid license -->
      <template v-if="canSaveConfig">
        <v-divider class="my-4" />
        <div class="text-right">
          <v-btn
            color="primary"
            :loading="isSaving"
            :disabled="isSaving || !canUpdateConfig"
            @click="saveConfig"
          >
            Update Configuration
          </v-btn>
        </div>
      </template>
    </v-form>
  </v-card>
</template>

<script>
export default {
  name: 'ModuleConfigForm',

  props: {
    customerId: {
      type: String,
      required: true,
    },
    customerCode: {
      type: String,
      required: true,
    },
    appCode: {
      type: String,
      required: true,
    },
    moduleName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      configId: null,
      moduleId: null,
      hasValidLicense: false, // Track if module license exists
      config: {},
      originalConfig: {},
      isLoading: true,
      isSaving: false,
      isFormValid: true,
      // OAuth state (for AI Order Import)
      oauthStatus: {
        isActive: false,
        tokenData: null,
      },
      isLoadingOAuthStatus: false,
      showImapPassword: false,
      isTestingImap: false,
      hasTestedCurrentConfig: false,
      isTestedWithSuccess: false,
      lastTestedConfig: {
        imapPath: '',
        username: '',
        password: '',
      },
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'Please enter a valid email address',
      ],
      bccEmailRules: [
        (v) => {
          if (!v || v.length === 0) return true;
          return v.every(email => /.+@.+\..+/.test(email)) || 'All entries must be valid email addresses';
        },
      ],
      maxAssortmentsRules: [
        v => v === null || v === '' || Number.isInteger(Number(v)) || 'Must be an integer',
        v => v === null || v === '' || Number(v) >= -1 || 'Must be -1 (unlimited) or a positive number',
      ],
      imapPathRules: [
        v => !!this.configId || !!v || 'IMAP path is required for new configuration',
        v => !v || /^[\w.-]+\.[\w.-]+:\d+\/imap\/[\w/-]+$/.test(v) || 'Please enter a valid IMAP path (e.g. mail.clustermail.de:993/imap/ssl/novalidate-cert)',
      ],
      imapUsernameRules: [
        v => !!this.configId || !!v || 'Username is required for new configuration',
      ],
      imapPasswordRules: [
        v => !!this.configId || !!v || 'Password is required for new configuration',
      ],
    };
  },

  watch: {
    moduleName() {
      this.resetAndReload();
    },
    appCode() {
      this.resetAndReload();
    },
    'config.imapPath': {
      handler() {
        this.resetImapTestFlags();
      },
    },
    'config.imapUsername': {
      handler() {
        this.resetImapTestFlags();
      },
    },
    'config.imapPassword': {
      handler() {
        this.resetImapTestFlags();
      },
    },
  },

  computed: {
    isModuleImplemented() {
      return (this.appCode === 'KOA' && this.moduleName === 'core')
        || (this.appCode === 'DDN' && this.moduleName === 'core')
        || (this.appCode === 'KOA' && this.moduleName === 'ai order import');
    },
    isAiOrderImportModule() {
      return this.appCode === 'KOA' && this.moduleName === 'ai order import';
    },
    // Only show save button if module is implemented AND has valid license
    canSaveConfig() {
      return this.isModuleImplemented && this.hasValidLicense;
    },
    hasChanges() {
      return Object.keys(this.getChangedFields()).length > 0;
    },
    canUpdateConfig() {
      if (!this.hasChanges) return false;
      if (this.imapConfigChanged && !this.isTestedWithSuccess) return false;
      return true;
    },
    isImapFormValid() {
      if (!this.isAiOrderImportModule) return true;
      if (this.config.emailServiceAuthType !== 'password') return true;
      return !!(this.config.imapPath && this.config.imapUsername && this.config.imapPassword);
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperuser'];
    },
    flowiseApiKeyHint() {
      if (this.config.flowiseApiKey === 'KEY_IS_SET') {
        return 'Customer-specific key configured. Enter new value to update, or clear to use global key.';
      }
      return 'Leave empty to use global Flowise API key';
    },
    flowiseApiKeyFieldType() {
      return this.config.flowiseApiKey === 'KEY_IS_SET' ? 'password' : 'text';
    },
    imapConfigChanged() {
      if (!this.isAiOrderImportModule) return false;
      if (this.config.emailServiceAuthType !== 'password') return false;
      return this.config.imapPath !== this.originalConfig.imapPath
        || this.config.imapUsername !== this.originalConfig.imapUsername
        || this.config.imapPassword !== this.originalConfig.imapPassword;
    },
  },

  methods: {
    resetAndReload() {
      this.configId = null;
      this.moduleId = null;
      this.hasValidLicense = false;
      this.config = {};
      this.originalConfig = {};
      this.oauthStatus = { isActive: false, tokenData: null };
      this.resetImapTestFlags();
      this.loadConfig();
      if (this.isAiOrderImportModule) {
        this.checkOAuthStatus();
      }
    },

    resetImapTestFlags() {
      const matchesOriginal = this.config.imapPath === this.originalConfig.imapPath
        && this.config.imapUsername === this.originalConfig.imapUsername
        && this.config.imapPassword === this.originalConfig.imapPassword;

      if (matchesOriginal) {
        this.hasTestedCurrentConfig = true;
        this.isTestedWithSuccess = true;
        this.lastTestedConfig = {
          imapPath: this.config.imapPath,
          username: this.config.imapUsername,
          password: this.config.imapPassword,
        };
      } else {
        this.hasTestedCurrentConfig = false;
        this.isTestedWithSuccess = false;
        this.lastTestedConfig = {
          imapPath: '',
          username: '',
          password: '',
        };
      }
    },

    getDefaultConfig() {
      if (this.appCode === 'KOA' && this.moduleName === 'core') {
        return {
          customerSpecificMailSender: false,
          deliveryMethodPickup: false,
          expressLogin: false,
          notificationMail: null,
          bccNotificationMail: [],
          mailFooterNote: null,
          orderingHints: null,
          maxAssortments: -1,
        };
      }

      if (this.appCode === 'DDN' && this.moduleName === 'core') {
        return {
          deliveryNoteFulfillment: false,
          pickupAndReturn: false,
          invoicedDeliveryNotes: false,
          separateDeliveryPointsByCustomer: true,
          internalNotesAndPhotos: false,
        };
      }

      if (this.appCode === 'KOA' && this.moduleName === 'ai order import') {
        return {
          emailServiceAuthType: 'password',
          defaultFoAccountId: null,
          supportLeadingZerosForFoProductId: false,
          imapPath: null,
          imapUsername: null,
          imapPassword: null,
          flowiseApiKey: null,
        };
      }

      return {};
    },

    normalizeValue(value) {
      if (value === '' || value === undefined) return null;
      if (Array.isArray(value) && value.length === 0) return null;
      return value;
    },

    areValuesEqual(val1, val2) {
      const norm1 = this.normalizeValue(val1);
      const norm2 = this.normalizeValue(val2);

      if (norm1 === null && norm2 === null) return true;

      if (Array.isArray(norm1) && Array.isArray(norm2)) {
        if (norm1.length !== norm2.length) return false;
        return norm1.every((item, index) => item === norm2[index]);
      }

      return norm1 === norm2;
    },

    getChangedFields() {
      const changedFields = {};
      const defaultConfig = this.getDefaultConfig();

      Object.keys(defaultConfig).forEach((key) => {
        if (!this.areValuesEqual(this.config[key], this.originalConfig[key])) {
          changedFields[key] = this.normalizeValue(this.config[key]);
        }
      });

      return changedFields;
    },

    sanitizePasswordFields(config) {
      const sanitized = { ...config };

      if (sanitized.imapPassword) {
        sanitized.imapPassword = null;
      }

      if (sanitized.flowiseApiKey) {
        sanitized.flowiseApiKey = 'KEY_IS_SET';
      }

      return sanitized;
    },

    async loadModuleIdFromLicense() {
      try {
        // 1. Load customer's licenses
        const response = await this.$http.get('/api/licences', {
          params: {
            'customer.id': this.customerId,
            itemsPerPage: 100,
          },
          headers: {
            Accept: 'application/ld+json',
          },
        });

        const licenses = response.data['hydra:member'] || [];

        // 2. Find license for the specific app
        const license = licenses.find(l => l.app && l.app.code === this.appCode);

        if (!license) {
          this.hasValidLicense = false;
          this.showAlertPopup(
            `No license found for ${this.getAppDisplayName(this.appCode)}. Please contact support.`,
            'error',
          );
          return false;
        }

        // 3. Find the specific module in the license
        const module = license.modules.find(m => m.name === this.moduleName);

        if (!module) {
          this.hasValidLicense = false;
          this.showAlertPopup(
            `Module "${this.formatModuleName(this.moduleName)}" not found in your ${this.getAppDisplayName(this.appCode)} license.`,
            'error',
          );
          return false;
        }

        // 4. Set the module ID and mark license as valid
        this.moduleId = module.id;
        this.hasValidLicense = true;
        return true;
      } catch (error) {
        this.hasValidLicense = false;
        this.showAlertPopup(
          'Failed to load license information. Please try again.',
          'error',
        );
        return false;
      }
    },

    async loadConfig() {
      this.isLoading = true;
      try {
        const moduleFound = await this.loadModuleIdFromLicense();
        if (!moduleFound) {
          this.isLoading = false;
          return;
        }

        const response = await this.$http.get('/api/customer_module_configs', {
          params: {
            'customer.code': this.customerCode,
            'module.app.code': this.appCode,
            'module.name': this.moduleName,
            page: 1,
            itemsPerPage: 30,
          },
          headers: {
            Accept: 'application/ld+json',
          },
        });

        const items = response.data['hydra:member'] || [];

        if (items.length > 0) {
          this.configId = items[0].id;
          const apiConfig = this.sanitizePasswordFields(items[0].config);
          this.config = { ...this.getDefaultConfig(), ...apiConfig };
          this.originalConfig = JSON.parse(JSON.stringify(this.config));
        } else {
          await this.autoCreateConfig();
        }
      } catch (error) {
        this.config = this.getDefaultConfig();
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
      } finally {
        this.isLoading = false;
      }
    },

    async autoCreateConfig() {
      if (!this.isModuleImplemented) {
        this.config = this.getDefaultConfig();
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
        return;
      }

      try {
        const response = await this.$http.post('/api/customer_module_configs', {
          customer: `/api/customers/${this.customerId}`,
          module: `/api/modules/${this.moduleId}`,
        });

        this.configId = response.data.id;
        const apiConfig = this.sanitizePasswordFields(response.data.config);
        this.config = { ...this.getDefaultConfig(), ...apiConfig };
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
      } catch (error) {
        this.config = this.getDefaultConfig();
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
      }
    },

    prepareConfigPayload() {
      return this.getChangedFields();
    },

    async saveConfig() {
      if (!this.$refs.form.validate()) {
        this.showAlertPopup('Please fix form errors before saving', 'error');
        return;
      }

      const changedFields = this.getChangedFields();
      if (Object.keys(changedFields).length === 0) {
        return;
      }

      if (this.isAiOrderImportModule && this.config.emailServiceAuthType === 'password') {
        if (this.imapConfigChanged && !this.isTestedWithSuccess) {
          this.showAlertPopup('Please test IMAP configuration before saving changes to IMAP fields', 'error');
          return;
        }
      }

      this.isSaving = true;
      try {
        const response = await this.$http.patch(
          `/api/customer_module_configs/${this.configId}`,
          { config: changedFields },
          {
            headers: {
              'Content-Type': 'application/merge-patch+json',
            },
          },
        );

        const apiConfig = this.sanitizePasswordFields(response.data.config);
        this.config = { ...this.getDefaultConfig(), ...this.config, ...apiConfig };
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
        this.resetImapTestFlags();
        this.showAlertPopup('Configuration updated successfully', 'info');
      } catch (error) {
        const errorMessage = error.response?.data?.['hydra:description']
          || error.response?.data?.detail
          || 'Failed to save configuration';
        this.showAlertPopup(errorMessage, 'error');
      } finally {
        this.isSaving = false;
      }
    },

    // OAuth methods (for AI Order Import)
    async checkOAuthStatus() {
      if (!this.isAiOrderImportModule) return;
      try {
        this.isLoadingOAuthStatus = true;
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const response = await this.$http.get(`${baseUrl}/api/oauth_tokens`, {
          params: {
            'customer.id': this.customerId,
            isActive: true,
          },
        });
        const list = (response && response.data && (response.data['hydra:member'] || response.data)) || [];
        if (Array.isArray(list) && list.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          this.oauthStatus.tokenData = list[0];
          this.oauthStatus.isActive = true;
        } else {
          this.oauthStatus.isActive = false;
          this.oauthStatus.tokenData = null;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking OAuth status:', error);
        this.oauthStatus.isActive = false;
        this.oauthStatus.tokenData = null;
      } finally {
        this.isLoadingOAuthStatus = false;
      }
    },

    async redirectToOAuthAuthorization() {
      try {
        const authorizeEndpoint = process.env.VUE_APP_OAUTH_MS_AUTHORIZE_URL;
        const url = `${authorizeEndpoint}?customerCode=${encodeURIComponent(this.customerCode)}`;
        window.location.assign(url);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('OAuth authorize redirect failed', e);
        this.showAlertPopup('Authorization redirect failed', 'error');
      }
    },

    checkAuthorizationResult() {
      if (!this.isAiOrderImportModule) return;
      const q = (this.$route && this.$route.query) ? this.$route.query : {};
      const success = q.oauth_success === 'true' || q.auth_success === 'true';
      const error = q.oauth_error === 'true' || q.auth_error === 'true';
      if (success) {
        this.showAlertPopup('OAuth authorization completed successfully!', 'success');
        this.checkOAuthStatus();
      }
      if (error) {
        this.showAlertPopup('OAuth authorization failed. Please try again.', 'error');
      }
      if (success || error) {
        this.$router.replace({ path: this.$route.path });
      }
    },

    // IMAP methods (for AI Order Import)
    async testImapConfig() {
      if (!this.isImapFormValid) {
        this.showAlertPopup('Please fill in all IMAP fields before testing', 'error');
        return;
      }
      try {
        this.isTestingImap = true;
        const baseUrl = process.env.VUE_APP_BASE_URL;
        const testEndpoint = '/api/imap_configurations/test';
        const testPayload = {
          imapPath: this.config.imapPath,
          username: this.config.imapUsername,
          password: this.config.imapPassword,
        };
        const response = await this.$http.post(`${baseUrl}${testEndpoint}`, testPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 200) {
          const responseData = response.data;
          if (responseData.status === 'fail') {
            this.showAlertPopup(`IMAP configuration test failed: ${responseData.message}`, 'error');
            this.hasTestedCurrentConfig = false;
            this.isTestedWithSuccess = false;
          } else {
            this.showAlertPopup('IMAP configuration test successful! You can now update the configuration.', 'success');
            this.hasTestedCurrentConfig = true;
            this.isTestedWithSuccess = true;
            this.lastTestedConfig = {
              imapPath: this.config.imapPath,
              username: this.config.imapUsername,
              password: this.config.imapPassword,
            };
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('IMAP configuration test failed:', error);
        this.showAlertPopup(`IMAP configuration test failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`, 'error');
      } finally {
        this.isTestingImap = false;
      }
    },

    getAppDisplayName(appCode) {
      const displayNames = {
        KOA: 'FoodOrder',
        DDN: 'Lieferschein',
        FOR1: 'FoodOffice R1',
      };
      return displayNames[appCode] || appCode;
    },

    formatModuleName(name) {
      return name
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    showAlertPopup(message, type = 'error') {
      this.$store.state.popupAlertStatus = true;
      this.$store.state.popupAlertMessage = message;
      this.$store.state.popupAlertType = type;
    },
  },

  async created() {
    await this.loadConfig();
    // AI Order Import: check OAuth status and handle callback
    if (this.isAiOrderImportModule) {
      await this.checkOAuthStatus();
      this.checkAuthorizationResult();
    }
  },
};
</script>

<style scoped>
.v-card-title {
  padding-bottom: 16px;
}
</style>
